"use client";

import React, { useState } from "react";
import { 
  IonPage, 
  IonContent, 
  IonHeader, 
  IonToolbar, 
  IonLoading, 
  useIonViewWillEnter 
} from "@ionic/react";
import { TreeView, TreeItem } from "@mui/lab";
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography, 
  Paper, 
  Box,
  Button,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";
import { useBusinessContext } from "../../../../context/business-context";

// Interface for account data
interface Account {
  id: number;
  coa_id: string;
  account_code: string;
  account_name: string;
  parent_id: string | null;
  account_class_id: string;
  financial_statement_id: string;
  is_postable: number;
  level: number;
  ledgerName?: string;
  children?: Account[];
}

// Interface for ledger group node
interface LedgerGroupNode {
  id: string;
  name: string;
  isGroup: true;
  children: Account[];
}

// Build tree structure grouped by ledgerName
const buildTreeGroupedByLedger = (accounts: Account[]): LedgerGroupNode[] => {
  // Group accounts by ledgerName
  const groupedByLedger = new Map<string, Account[]>();
  
  accounts.forEach(account => {
    const ledgerName = account.ledgerName || "Uncategorized";
    if (!groupedByLedger.has(ledgerName)) {
      groupedByLedger.set(ledgerName, []);
    }
    groupedByLedger.get(ledgerName)!.push(account);
  });

  // Build tree for each ledger group
  const ledgerGroups: LedgerGroupNode[] = [];
  
  groupedByLedger.forEach((groupAccounts, ledgerName) => {
    // Build hierarchical tree within this group
    const map = new Map<string, Account>();
    const roots: Account[] = [];

    // First pass: create a map of all accounts with children array
    groupAccounts.forEach(account => {
      map.set(account.coa_id, { ...account, children: [] });
    });

    // Second pass: build the tree structure
    groupAccounts.forEach(account => {
      const node = map.get(account.coa_id);
      if (!node) return;

      if (!account.parent_id || account.parent_id === null || account.parent_id === "") {
        // Top-level account within this ledger group
        roots.push(node);
      } else {
        // Child account - find parent and add to its children
        const parent = map.get(account.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
        } else {
          // Parent not found in this group, treat as root
          roots.push(node);
        }
      }
    });

    // Create ledger group node
    ledgerGroups.push({
      id: `ledger_group_${ledgerName}`,
      name: ledgerName,
      isGroup: true,
      children: roots
    });
  });

  return ledgerGroups;
};

// Recursive render function for account tree
const renderAccountTree = (node: Account): JSX.Element => (
  <TreeItem 
    key={node.coa_id} 
    nodeId={node.coa_id} 
    label={`${node.account_code} - ${node.account_name}`}
  >
    {node.children && node.children.length > 0
      ? node.children.map((child) => renderAccountTree(child))
      : null}
  </TreeItem>
);

// Render function for ledger group
const renderLedgerGroup = (group: LedgerGroupNode,index:number): JSX.Element => (
  <TreeItem
    key={index}
    nodeId={group.id}
    label={
      <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
        <FolderIcon sx={{ mr: 1, color: '#f59e0b' }} />
        <Typography sx={{ fontWeight: 600, color: '#1f2937' }}>
          {group.name} ({group.children.length})
        </Typography>
      </Box>
    }
  >
    {group.children.map((account) => renderAccountTree(account))}
  </TreeItem>
);

// Helper function to find account in grouped tree
const findAccountInTree = (groups: LedgerGroupNode[], coaId: string): Account | null => {
  for (const group of groups) {
    const found = findAccountInGroup(group.children, coaId);
    if (found) return found;
  }
  return null;
};

// Helper to find account within a group's accounts
const findAccountInGroup = (accounts: Account[], coaId: string): Account | null => {
  for (const account of accounts) {
    if (account.coa_id === coaId) return account;
    if (account.children) {
      const found = findAccountInGroup(account.children, coaId);
      if (found) return found;
    }
  }
  return null;
};

const ChartOfAccountsPage: React.FC = () => {
  const { selectedBusinessId, selectedBusinessName } = useBusinessContext();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // COA data from API
  const [chartOfAccountsFlat, setChartOfAccountsFlat] = useState<Account[]>([]);
  const [chartOfAccountsTree, setChartOfAccountsTree] = useState<LedgerGroupNode[]>([]);
  const [selectedParent, setSelectedParent] = useState<Account | null>(null);

  // API data states
  const [ledgerMother2, setLedgerMother2] = useState<any[]>([]);
  const [motherComp, setMotherComp] = useState<any[]>([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subheadingType, setSubheadingType] = useState("");
  const [mainheadingType, setMainheadingType] = useState("");
  const [ledgerType, setLedgerType] = useState("motherComp");
  
  // Account details (only for subheadings)
  const [accountDetails, setAccountDetails] = useState({
    account_code: "",
    account_name: "",
    is_postable: true
  });
  
  // Alert states
  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [status, setStatus] = useState({ loading: false, error: false });

  // CSV Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<any[]>([]);

  const getChartOfAccounts = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
    formData.append("business_id", selectedBusinessId);
    setStatus({ loading: true, error: false });
    
    axios
      .post(baseUrl() + "Geacloud_ChartOfAccounts/getCOA", formData, config)
      .then((response) => {
        console.log("Chart of Accounts:", response);
        const accounts = response.data.data || [];
        setChartOfAccountsFlat(accounts);
        
        // Build tree structure grouped by ledger name
        const tree = buildTreeGroupedByLedger(accounts);
        setChartOfAccountsTree(tree);
        
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        setStatus({ loading: false, error: false });
        console.error("Error fetching chart of accounts:", error);
      });
  };

  const getLedgers = () => {
    const formData = new FormData();
    formData.append("requestID", requestID());
    setStatus({ loading: true, error: false });
    
    axios
      .post(baseUrl() + "ledgercreates/getLedgerMothers_byBranch", formData, config)
      .then((response) => {
        console.log("Ledgers:", response);
        setLedgerMother2(response.data.getLedgerMothers2 || []);
        setMotherComp(response.data.getMotherComp || []);
        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        setStatus({ loading: false, error: false });
        console.error("Error fetching ledgers:", error);
      });
  };

  const onShowAlert = () => {
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const resetModalForm = () => {
    setSubheadingType("");
    setMainheadingType("");
    setLedgerType("motherComp");
    setAccountDetails({
      account_code: "",
      account_name: "",
      is_postable: true
    });
  };

  const onAddLedger = async () => {
    try {
      setStatus({ loading: true, error: false });
      
      let response;
      const mainHeading = motherComp?.find((comp: any) => comp.compID === mainheadingType) || null;

      // If creating a subheading, use Chart of Accounts endpoint
      if (ledgerType === "motherSub") {
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("business_id", selectedBusinessId);
        formData.append("mother_id", subheadingType);
        formData.append("setupType", ledgerType);
        formData.append("motherComp", mainheadingType);
        
        // Use selected parent from tree if available
        formData.append("parent_id", selectedParent?.coa_id || "");
        formData.append("parent_account_code", selectedParent?.account_code || "");
        
        formData.append("motherSub", accountDetails.account_name);
        formData.append("account_code", accountDetails.account_code);
        formData.append("account_name", accountDetails.account_name);
        
        if (mainHeading) {
          formData.append("account_class", mainHeading.name);
          formData.append("account_class_id", mainHeading.compID);
        }
        formData.append("is_postable", accountDetails.is_postable ? "1" : "0");

        response = await axios.post(
          baseUrl() + "Geacloud_ChartOfAccounts/createChartOfAccount",
          formData,
          config
        );

        console.log({response});
      } else {
        // Main heading - use old endpoint
        const formData = new FormData();
        formData.append("requestID", requestID());
        formData.append("business_id", selectedBusinessId);
        formData.append("mother_id", subheadingType);
        formData.append("setupType", ledgerType);
        formData.append("motherComp", mainheadingType);
        formData.append("parent_id", "");

        response = await axios.post(
          baseUrl() + "ledgercreates/addLedgerHead_Subhead",
          formData,
          config
        );
      }
      
      const data = await response.data;

      if (data.status == true) {
        setAlertMsg(data.message || "Ledger heading added successfully!");
        resetModalForm();
        onShowAlert();
        setStatus({ loading: false, error: false });
        getLedgers();
        getChartOfAccounts();
      }
      setIsModalOpen(false);
    } catch (error: any) {
      setStatus({ loading: false, error: true });
      console.log(error);
      setAlertMsg(error.response?.data?.message || "An error occurred.");
      onShowAlert();
    } finally {
      setStatus({ loading: false, error: false });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setAlertMsg("Please select a CSV file first");
      setShowAlert(true);
      return;
    }

    try {
      setStatus({ loading: true, error: false });
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusinessId);
      formData.append("csv_file", selectedFile);

      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const response = await axios.post(
        baseUrl() + "ledgercreates/bulkUploadAccounts",
        formData,
        {
          ...config,
          headers: {
            ...config.headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      clearInterval(interval);
      setUploadProgress(100);

      const data = await response.data;
      setUploadResults(data.results || []);
      setAlertMsg(data.message || "CSV uploaded successfully!");
      setShowAlert(true);
      setSelectedFile(null);
      setStatus({ loading: false, error: false });
      
      getChartOfAccounts();
    } catch (error: any) {
      setStatus({ loading: false, error: true });
      setAlertMsg(error.response?.data?.message || "Upload failed");
      setShowAlert(true);
    } finally {
      setUploadProgress(0);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = "account_code,account_name,account_class,parent_id,is_postable\n1000,Cash,ASSET,,1\n2000,Accounts Payable,LIABILITY,,1";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chart_of_accounts_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  useIonViewWillEnter(() => {
    getLedgers();
    getChartOfAccounts();
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ color: "#2c3e50", fontWeight: 600 }}>
              {selectedBusinessName?.toUpperCase()} - CHART OF ACCOUNTS
            </Typography>
          </Box>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3, backgroundColor: "#f5f7fa", minHeight: "100%" }}>
          {/* Alert */}
          {showAlert && (
            <Alert 
              severity={status.error ? "error" : "success"} 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setShowAlert(false)}
            >
              <AlertTitle>{status.error ? "Error" : "Success"}</AlertTitle>
              {alertMsg}
            </Alert>
          )}

          {/* Tabs */}
          <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  minHeight: 64
                }
              }}
            >
              <Tab label="Chart of Accounts" icon={<FolderIcon />} iconPosition="start" />
              <Tab label="Bulk Upload" icon={<CloudUploadIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Left Panel - Tree View */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50" }}>
                        Chart of Accounts
                      </Typography>
                      <Chip 
                        label={`${chartOfAccountsFlat.length} accounts`} 
                        color="primary" 
                        size="small" 
                      />
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    
                    {chartOfAccountsTree.length > 0 ? (
                      <TreeView
                        defaultCollapseIcon={<span style={{ fontWeight: 'bold' }}>−</span>}
                        defaultExpandIcon={<span style={{ fontWeight: 'bold' }}>+</span>}
                        selected={selectedParent?.coa_id || ""}
                        onNodeSelect={(_, nodeId) => {
                          const account = findAccountInTree(chartOfAccountsTree, nodeId);
                          if (account) {
                            setSelectedParent(account);
                          }
                        }}
                        sx={{
                          '& .MuiTreeItem-content': {
                            padding: '8px',
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: '#e3f2fd'
                            }
                          },
                          '& .Mui-selected': {
                            backgroundColor: '#bbdefb !important'
                          }
                        }}
                      >
                        {chartOfAccountsTree.map((group,index) => renderLedgerGroup(group,index))}
                      </TreeView>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No accounts found. Create your first account.
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedParent && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f4f8', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Selected Parent: <strong>{selectedParent.account_code} - {selectedParent.account_name}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          New subheadings will be created under this parent
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedParent(null)}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Clear Selection
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Panel - Add Heading Button */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50" }}>
                        Manage Accounts
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => setIsModalOpen(true)}
                        sx={{
                          backgroundColor: '#10b981',
                          '&:hover': { backgroundColor: '#059669' },
                          textTransform: 'none',
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          fontSize: '1rem'
                        }}
                      >
                        Add Ledger Heading / Account
                      </Button>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Create main headings or subheadings with account details
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Bulk Upload Tab */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 2 }}>
                      Upload CSV File
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Box
                      sx={{
                        border: '2px dashed #cbd5e1',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: '#f8fafc',
                        mb: 3,
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#3b82f6',
                          backgroundColor: '#eff6ff'
                        }
                      }}
                      onClick={() => document.getElementById('csv-upload')?.click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                        {selectedFile ? selectedFile.name : 'Click to select CSV file'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        or drag and drop your file here
                      </Typography>
                      <input
                        id="csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                      />
                    </Box>

                    {uploadProgress > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Uploading...</Typography>
                          <Typography variant="body2">{uploadProgress}%</Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 8, backgroundColor: '#e2e8f0', borderRadius: 1 }}>
                          <Box
                            sx={{
                              width: `${uploadProgress}%`,
                              height: '100%',
                              backgroundColor: '#3b82f6',
                              borderRadius: 1,
                              transition: 'width 0.3s'
                            }}
                          />
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={downloadCSVTemplate}
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          borderRadius: 2,
                          py: 1.5,
                          borderColor: '#cbd5e1',
                          color: '#475569',
                          '&:hover': {
                            borderColor: '#94a3b8',
                            backgroundColor: '#f8fafc'
                          }
                        }}
                      >
                        Download Template
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={handleFileUpload}
                        disabled={!selectedFile}
                        fullWidth
                        sx={{
                          backgroundColor: '#3b82f6',
                          '&:hover': { backgroundColor: '#2563eb' },
                          textTransform: 'none',
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600
                        }}
                      >
                        Upload CSV
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 2 }}>
                      CSV Upload Instructions
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Typography variant="body2" paragraph>
                      <strong>Required Columns:</strong>
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                      <li><Typography variant="body2"><code>account_code</code> - Unique account code</Typography></li>
                      <li><Typography variant="body2"><code>account_name</code> - Account name</Typography></li>
                      <li><Typography variant="body2"><code>account_class</code> - ASSET, LIABILITY, EQUITY, REVENUE, or EXPENSE</Typography></li>
                      <li><Typography variant="body2"><code>parent_id</code> - Parent account code (optional)</Typography></li>
                      <li><Typography variant="body2"><code>is_postable</code> - 1 for Yes, 0 for No</Typography></li>
                    </Box>

                    <Typography variant="body2" paragraph>
                      <strong>Example:</strong>
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: '#f8fafc', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      account_code,account_name,account_class,parent_id,is_postable<br/>
                      1000,Cash,ASSET,,1<br/>
                      2000,Accounts Payable,LIABILITY,,1
                    </Paper>

                    <Alert severity="info" sx={{ mt: 3 }}>
                      Download the template to get started with the correct format.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>

              {uploadResults.length > 0 && (
                <Grid item xs={12}>
                  <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "#2c3e50", mb: 2 }}>
                        Upload Results
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      {uploadResults.map((result, index) => (
                        <Alert 
                          key={index} 
                          severity={result.success ? "success" : "error"}
                          sx={{ mb: 1 }}
                        >
                          Row {result.row}: {result.message}
                        </Alert>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}

          {/* Add Ledger Headings Modal */}
          <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                maxHeight: '90vh',
                width: 900,
                maxWidth: '90vw',
                bgcolor: "white",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                p: 4,
                borderRadius: 3,
                overflow: "auto"
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: "#2c3e50" }}>
                Add Ledger Heading / Account
              </Typography>

              {showAlert && (
                <Alert severity={status.error ? "error" : "success"} sx={{ mb: 3 }}>
                  <AlertTitle>{status.error ? "Error" : "Success"}</AlertTitle>
                  {alertMsg}
                </Alert>
              )}

              {/* Select Ledger Mother Type */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Ledger Mother Type</InputLabel>
                <Select
                  value={subheadingType}
                  label="Select Ledger Mother Type"
                  onChange={(e) => setSubheadingType(e.target.value)}
                >
                  {ledgerMother2?.map((ledger: any, index) => (
                    <MenuItem key={index} value={ledger.motherID}>
                      {ledger.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Radio buttons for Main Heading vs Subheading */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <RadioGroup
                  row
                  value={ledgerType}
                  onChange={(e) => {
                    setLedgerType(e.target.value);
                    setMainheadingType("");
                  }}
                >
                  <FormControlLabel
                    value="motherComp"
                    control={<Radio />}
                    label="Main Heading (No account details needed)"
                  />
                  <FormControlLabel
                    value="motherSub"
                    control={<Radio />}
                    label="Subheading (Requires account details)"
                  />
                </RadioGroup>
              </FormControl>

              {/* Main Heading - Just name */}
              {ledgerType === "motherComp" && (
                <TextField
                  fullWidth
                  label="Enter Main Heading Name"
                  variant="outlined"
                  value={mainheadingType}
                  onChange={(e) => setMainheadingType(e.target.value)}
                  sx={{ mb: 3 }}
                />
              )}

              {/* Subheading - Select main heading + account details */}
              {ledgerType === "motherSub" && (
                <>
                  {selectedParent && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <strong>Selected Parent:</strong> {selectedParent.account_code} - {selectedParent.account_name}
                      <br />
                      <Typography variant="caption">
                        This subheading will be created under the selected parent from the tree.
                      </Typography>
                    </Alert>
                  )}
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Select Main Heading</InputLabel>
                    <Select
                      value={mainheadingType}
                      label="Select Main Heading"
                      onChange={(e) => setMainheadingType(e.target.value)}
                    >
                      {motherComp
                        ?.filter((comp: any) => comp.motherID === subheadingType)
                        .map((ledger: any, index) => (
                          <MenuItem key={index} value={ledger.compID}>
                            {ledger.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: "#2c3e50" }}>
                    Account Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Code"
                        variant="outlined"
                        value={accountDetails.account_code}
                        onChange={(e) => setAccountDetails({...accountDetails, account_code: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Name"
                        variant="outlined"
                        value={accountDetails.account_name}
                        onChange={(e) => setAccountDetails({...accountDetails, account_name: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Class"
                        variant="outlined"
                        value={motherComp?.find((comp: any) => comp.compID === mainheadingType)?.name || ''}
                        disabled
                        helperText="Auto-filled from selected main heading"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Postable?</InputLabel>
                        <Select
                          value={accountDetails.is_postable ? "1" : "0"}
                          label="Postable?"
                          onChange={(e) => setAccountDetails({...accountDetails, is_postable: e.target.value === "1"})}
                        >
                          <MenuItem value="1">Yes</MenuItem>
                          <MenuItem value="0">No</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetModalForm();
                  }}
                  sx={{ textTransform: 'none', px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onAddLedger}
                  disabled={!subheadingType || !mainheadingType || (ledgerType === "motherSub" && (!accountDetails.account_code || !accountDetails.account_name))}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': { backgroundColor: '#059669' },
                    textTransform: 'none',
                    px: 4
                  }}
                >
                  {status.loading ? "Loading..." : "Submit"}
                </Button>
              </Box>
            </Box>
          </Modal>

          <IonLoading
            message="Loading..."
            spinner="circles"
            isOpen={status.loading}
          />
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default ChartOfAccountsPage;
